import React, { useState, useEffect, useCallback } from 'react';
import { HiHeart, HiPlay } from 'react-icons/hi';
import SearchableDropdown from './SearchableDropdown';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VoiceDropdown = ({ onSelect, selectedVoice: externalSelectedVoice = null }) => {
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(externalSelectedVoice);
  const [voiceCategory, setVoiceCategory] = useState('curated');
  const [loading, setLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [globalSampleText] = useState('Hello, this is a sample of my voice.');

  useEffect(() => {
    fetchVoices();
  }, []);

  useEffect(() => {
    setSelectedVoice(externalSelectedVoice);
  }, [externalSelectedVoice]);

  const fetchVoices = async () => {
    try {
      setLoading(true);
      const voicesResponse = await api.getVoices();
      if (voicesResponse.voices) {
        setVoices(voicesResponse.voices);
      }
    } catch (error) {
      console.error('Error fetching voices:', error);
      toast.error('Failed to fetch voices');
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceSelect = (voice) => {
    setSelectedVoice(voice);
    if (onSelect) {
      onSelect(voice);
    }
  };

  const handleGenerateSample = async (voice) => {
    try {
      if (!globalSampleText || audioPlaying) return;
      
      setLoading(true);
      setAudioPlaying(true);
      
      const audioBlob = await api.generateVoiceSample(
        voice.id,
        globalSampleText,
        null,
        'en-US'
      );

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.play();
      
      audio.onended = () => {
        setAudioPlaying(false);
        URL.revokeObjectURL(audioUrl);
        setLoading(false);
      };

    } catch (err) {
      toast.error(err.message);
      setAudioPlaying(false);
      setLoading(false);
    }
  };

  const filterVoiceOptions = useCallback(
    (options, { inputValue }) => {
      const q = (inputValue || '').toLowerCase().trim();
      const matchesSearch = (voice) => {
        if (!q) return true;
        return (
          (voice.name || '').toLowerCase().includes(q) ||
          (voice.description || '').toLowerCase().includes(q) ||
          (voice.tags || []).some((tag) => (tag || '').toLowerCase().includes(q))
        );
      };

      return options.filter((voice) => {
        if (!matchesSearch(voice)) return false;
        switch (voiceCategory) {
          case 'curated':
            return voice.tags?.includes('Curated by us');
          case 'your':
            return voice.is_user_voice;
          case 'favorites':
            return voice.is_favorite;
          case 'all':
            return voice.public;
          default:
            return false;
        }
      });
    },
    [voiceCategory]
  );

  return (
    <SearchableDropdown
      options={voices}
      filterOptions={filterVoiceOptions}
      onSelect={handleVoiceSelect}
      selectedOption={selectedVoice}
      placeholder="Select a voice"
      searchPlaceholder="Search voices…"
      displayKey="name"
      className="mb-2"
      isLoading={loading}
      headerContent={
        <div className="flex flex-wrap gap-2 p-3 border-b border-[var(--app-border)] bg-[var(--app-surface-veil)]">
          {[
            { id: 'curated', name: 'Curated' },
            { id: 'your', name: 'Your Voices' },
            { id: 'favorites', name: 'Favorites' },
            { id: 'all', name: 'All' },
          ].map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => setVoiceCategory(category.id)}
              className={`
                px-3 py-1 text-sm font-medium rounded-full border transition-colors
                ${
                  voiceCategory === category.id
                    ? 'border-[var(--app-accent)] bg-[var(--app-accent-soft)] text-[var(--app-accent)]'
                    : 'border-[var(--app-border)] bg-[var(--app-control-bg)] text-[var(--app-control-fg)] hover:bg-[var(--app-control-bg-hover)]'
                }
              `}
            >
              {category.name}
            </button>
          ))}
        </div>
      }
      renderOption={(voice) => (
        <div className="py-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-medium text-[var(--app-ink)]">
                {voice.name}
              </div>
              {voice.description && (
                <div className="text-sm text-[var(--app-muted)]">{voice.description}</div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateSample(voice);
                }}
                disabled={audioPlaying || loading}
                className="p-2 text-[var(--app-muted)] hover:text-[var(--app-accent)] disabled:opacity-50"
                title="Play sample"
              >
                <HiPlay className="h-5 w-5" />
              </button>
              {voice.is_favorite && <HiHeart className="h-5 w-5 text-red-500" />}
            </div>
          </div>
          {voice.tags && voice.tags.length > 0 && (
            <div className="flex gap-1 mt-1">
              {voice.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--app-accent-soft)] text-[var(--app-accent)]"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    />
  );
};

export default VoiceDropdown; 